import { Injectable } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import moment, { Moment, isMoment } from "moment";
import { BehaviorSubject, Observable } from "rxjs";
import { ApiGateway } from "../cw-backend/api-gateway";

@Injectable()
export class CwUtils {
    private onlineStatus: BehaviorSubject<boolean>;

    constructor(public gw: ApiGateway) {
        this.onlineStatus = new BehaviorSubject<boolean>(navigator.onLine);
        window.addEventListener("online", () => this.updateOnlineStatus());
        window.addEventListener("offline", () => this.updateOnlineStatus());
    }

    public static checkDate(date) {
        if (date instanceof Date) {
            return this.formatDate(date);
        } else return date;
    }

    public static momentToDate(moment) {
        if (isMoment(moment)) {
            return moment.toDate();
        }
    }

    public static momentFormat(date) {
        if (moment(date).isValid()) {
            return moment(date).format("YYYY-MM-DD");
        } else {
            return null;
        }
    }

    public static dateConverterToLocalDate(accessDate: Moment, hours?): any {
        if (accessDate.isValid && !hours) {
            return accessDate.format("YYYY-MM-DD");
        } else if (accessDate.isValid && hours) {
            accessDate.set({
                hours: hours[0] + hours[1],
                minutes: hours[3] + hours[4],
            });
            return accessDate.format("YYYY-MM-DDTHH:mm");
        } else {
            return accessDate;
        }
    }

    public static dateConverterToTimestamp(accessDate: Moment, hours?): any {
        if (accessDate.isValid) {
            return accessDate.format("x");
        } else {
            return accessDate;
        }
    }

    public static dateConverter(accessDate, hours?): any {
        let date: Date = new Date();
        let data = date.setFullYear(
            accessDate.year,
            accessDate.monthValue - 1,
            accessDate.dayOfMonth,
        );
        if (hours) {
            date.setHours(
                accessDate.hour,
                accessDate.minute,
                accessDate.second,
            );
        }
        return date;
    }

    public static dateArrayConverter(accessDate, hour?): any {
        let date: Date;
        date = new Date();
        let month: any = accessDate[1];
        if (month < 10) {
            month = "0" + month;
        }
        date.setFullYear(accessDate[0], month - 1, accessDate[2]);
        if (hour) {
            date.setHours(accessDate[3], accessDate[4], 0o0);
        }
        return date;
    }

    public static calcAge(birthday) {
        return moment().diff(moment(birthday, "YYYYMMDD"), "years");
    }

    // CONVERT string dd/mm/yyyy para date
    public static formatStringDate(date, campo = "Data"): Date {
        if (!date || typeof date != "string") {
            throw new Error(`Campo ${campo} com dados inválidos ou vazio.`);
        }

        const dateString = date;
        const [day, month, year] = dateString.split("/").map(Number);
        return (date = new Date(year, month - 1, day));
    }

    public static formatDate(date, campo = "Data"): string {
        if (!date || typeof date == "string") {
            throw new Error(`Campo ${campo} com dados inválidos ou vazio.`);
        }
        var dd = date.getDate();
        var mm = date.getMonth() + 1;
        var yyyy = date.getFullYear();
        if (dd < 10) {
            dd = "0" + dd;
        }

        if (mm < 10) {
            mm = "0" + mm;
        }

        return yyyy + "-" + mm + "-" + dd;
    }

    public static compare(arr1, arr2) {
        if (!arr1 || !arr2) return true;
        let result;
        arr1?.forEach((e1, i) =>
            arr2.forEach((e2) => {
                if (e1.length > 1 && e2.length) {
                    result = this.compare(e1, e2);
                } else if (e1.id !== e2.id) {
                    result = false;
                } else {
                    result = true;
                }
            }),
        );
        return result;
    }

    public static getPageOptions(matPaginator, matSort, filter): any {
        let pageOptions = new PageOptions();
        pageOptions.sizePage = matPaginator.pageSize;
        pageOptions.page = matPaginator.pageIndex;

        if (matSort.active && matSort.direction) {
            pageOptions.fieldSort = matSort.active;
            pageOptions.sortDirection = matSort.direction.toString();
        }
        if (filter) {
            pageOptions.argument = filter;
        }
        return pageOptions;
    }

    public static setMatPaginator(
        matPaginator: MatPaginator,
        pageOptions,
    ): MatPaginator {
        matPaginator.pageIndex = pageOptions.page;
        matPaginator.pageSize = pageOptions.sizePage;
        return matPaginator;
    }

    public static setMatSort(matSort, pageOptions): MatSort {
        matSort.active = pageOptions.fieldSort;
        if (pageOptions.sortDirection == "asc") {
            matSort.direction = "asc";
        } else {
            matSort.direction = "desc";
        }
        return matSort;
    }

    public static comparePageOptions() {
        if (
            location.pathname
                .toString()
                .includes(sessionStorage.getItem("navigation"))
        ) {
            return false;
        } else {
            return true;
        }
    }

    public static checkPage(path) {
        if (location.pathname.includes(path) || !path || !location.pathname) {
            return true;
        }
        return false;
    }

    public static isObject(value: any): value is object {
        return value !== null && typeof value === "object";
    }

    public static extractId(object: any) {
        if (!object?.id) {
            return null;
        }

        return { id: object.id };
    }

    public static extractOnlyId(object: any) {
        if (!object?.id) {
            return null;
        }

        return object.id;
    }

    public static checkStringIsNotNull(str: string | null | undefined) {
        return str !== null && str !== undefined && str.trim().length > 0;
    }

    private updateOnlineStatus() {
        this.onlineStatus.next(navigator.onLine);
    }

    getOnlineStatus(): Observable<boolean> {
        return this.onlineStatus.asObservable();
    }

    public static makeKey(
        id: number | string,
        table: string,
        tenant: string,
    ): string {
        const raw = `${id}:${table}:${tenant}`;
        return this.encodeBase64(raw);
    }

    public static parseKey(key: string): {
        id: string;
        table: string;
        tenant: string;
    } {
        const decoded = this.decodeBase64(key);
        const [id, table, tenant] = decoded.split(":");
        return { id, table, tenant };
    }

    public static encodeBase64(data: string): string {
        const encoder = new TextEncoder();
        const bytes = encoder.encode(data);
        let binary = "";
        bytes.forEach((b) => (binary += String.fromCharCode(b)));
        return window.btoa(binary);
    }

    public static decodeBase64(base64: string): string {
        const binary = window.atob(base64);
        const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
        const decoder = new TextDecoder();
        return decoder.decode(bytes);
    }

    public static formatXML(xml: string) {
        const PADDING = "  "; // 2 espaços
        const reg = /(>)(<)(\/*)/g;
        let formatted = "";
        let pad = 0;

        // adiciona quebra de linha entre tags adjacentes
        xml = xml.replace(reg, "$1\r\n$2$3");
        xml.split("\r\n").forEach((node) => {
            let indent = 0;
            if (node.match(/.+<\/\w[^>]*>$/)) {
                indent = 0;
            } else if (node.match(/^<\/\w/)) {
                if (pad !== 0) pad -= 1;
            } else if (node.match(/^<\w[^>]*[^\/]>.*$/)) {
                indent = 1;
            } else {
                indent = 0;
            }

            const padding = PADDING.repeat(pad);
            formatted += padding + node + "\r\n";
            pad += indent;
        });

        return formatted.trim();
    }

    /* getLatency(): Promise<number> {
        const start = Date.now();
        return this.gw
            .get("util/ping")
            .toPromise()
            .then((response) => Date.now() - start)
            .catch(() => -1); // Retorna -1 em caso de erro na requisição
    }*/

    public static async imprimirBase64(base64: string): Promise<void> {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        return CwUtils.imprimirBlob(new Blob([byteArray], { type: "application/pdf" }));
    }

    public static imprimirBlob(blob: Blob): Promise<void> {
        return new Promise((resolve) => {
            const fileURL = URL.createObjectURL(blob);

            const iframe = document.createElement("iframe");
            iframe.style.position = "fixed";
            iframe.style.right = "0";
            iframe.style.bottom = "0";
            iframe.style.width = "1px";
            iframe.style.height = "1px";
            iframe.style.opacity = "0";
            iframe.style.border = "0";
            iframe.src = fileURL;

            document.body.appendChild(iframe);

            let resolved = false;

            const finish = () => {
                if (resolved) {
                    return;
                }

                resolved = true;
                window.removeEventListener("focus", onFocus);

                setTimeout(() => {
                    try {
                        URL.revokeObjectURL(fileURL);
                    } catch (e) { }

                    try {
                        if (iframe.parentNode) {
                            iframe.parentNode.removeChild(iframe);
                        }
                    } catch (e) { }

                    resolve();
                }, 300);
            };

            const onFocus = () => {
                setTimeout(() => {
                    finish();
                }, 1000);
            };

            iframe.onload = () => {
                setTimeout(() => {
                    window.addEventListener("focus", onFocus, { once: true });

                    try {
                        iframe.contentWindow?.focus();
                        iframe.contentWindow?.print();
                    } catch (e) {
                        finish();
                        return;
                    }

                    // fallback caso o navegador não devolva foco corretamente
                    setTimeout(() => {
                        finish();
                    }, 15000);
                }, 800);
            };
        });
    }
}

export class PageOptions {
    argument: any = "";
    page: number = 0;
    sizePage: number = 50;
    fieldSort?: string = "name";
    sortDirection: string = "asc";
    type?: string;
    active?: boolean;
}
